import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Heart, Droplets, Activity, Weight, Plus, Video,
  Clock, TrendingUp, Check, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { parametriVitali } from '@/data/mockPatientData';

/* ══════ CONFIG SEMAFORO ══════ */
const semaforoConfig = {
  verde: { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'Tutto ok' },
  giallo: { bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500', text: 'text-amber-700', label: 'Attenzione' },
  rosso: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', text: 'text-red-700', label: 'Fuori norma' },
};

const paramIcons = {
  pressione: Heart,
  spo2: Activity,
  glicemia: Droplets,
  peso: Weight,
};

const paramColors = {
  pressione: '#EF4444',
  spo2: '#3B82F6',
  glicemia: '#F59E0B',
  peso: '#10B981',
};

/* ══════ CARD PARAMETRO ══════ */
function VitalCard({ param, index, onAdd }) {
  const sem = semaforoConfig[param.stato];
  const Icon = paramIcons[param.id] || Heart;
  const color = paramColors[param.id];
  const msg = param[`messaggio${param.stato.charAt(0).toUpperCase() + param.stato.slice(1)}`];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
    >
      <Card
        className={`border-2 shadow-[0_2px_10px_rgba(0,0,0,0.04)] rounded-3xl overflow-hidden transition-shadow hover:shadow-md ${sem.border}`}
        data-testid={`vital-card-${param.id}`}
      >
        <CardContent className="p-0">
          {/* Semaforo top bar */}
          <div className={`px-5 py-2.5 flex items-center gap-2 ${sem.bg}`} data-testid={`semaforo-${param.id}`}>
            <div className={`w-3 h-3 rounded-full ${sem.dot} ${param.stato === 'rosso' ? 'animate-pulse' : ''}`} />
            <span className={`text-xs font-bold ${sem.text}`}>{msg}</span>
          </div>

          <div className="p-5 space-y-4">
            {/* Header */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
                <Icon className="w-6 h-6" style={{ color }} />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">{param.label}</p>
                <p className="text-[10px] text-muted-foreground">{param.soglia}</p>
              </div>
            </div>

            {/* Valore principale */}
            <div className="text-center py-2">
              <p className="text-4xl font-extrabold text-foreground tracking-tight">{param.valore}</p>
              <p className="text-sm text-muted-foreground font-medium mt-0.5">{param.unita}</p>
            </div>

            {/* Ultima rilevazione */}
            <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
              <Clock className="w-3 h-3" />{param.ultimaRilevazione}
            </div>

            {/* Mini storico */}
            <div className="flex items-center gap-2 justify-center flex-wrap">
              {param.storico.slice(1).map((s, i) => (
                <span key={i} className="text-[9px] px-2 py-1 rounded-full bg-muted/30 text-muted-foreground font-medium">
                  {s.data}: {s.valore}
                </span>
              ))}
            </div>

            {/* Bottone Aggiungi */}
            <button
              onClick={() => onAdd(param)}
              className="w-full flex items-center justify-center gap-2.5 h-12 rounded-2xl bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-bold text-sm shadow-sm transition-all active:scale-[0.98]"
              data-testid={`btn-aggiungi-${param.id}`}
            >
              <Plus className="w-5 h-5" />Aggiungi Misurazione
            </button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/* ══════ MODALE INSERIMENTO ══════ */
function InputModal({ param, onClose, onSave }) {
  const [valore, setValore] = useState('');
  const Icon = paramIcons[param.id] || Heart;
  const color = paramColors[param.id];

  const handleSave = () => {
    if (!valore.trim()) return;
    onSave(param.id, valore);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="input-modal-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
        className="bg-card rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="input-modal-panel"
      >
        <div className="p-7 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{param.label}</p>
              <p className="text-xs text-muted-foreground">Inserisci il nuovo valore ({param.unita})</p>
            </div>
          </div>

          <input
            type="text"
            inputMode="decimal"
            autoFocus
            className="w-full h-16 rounded-2xl border-2 border-border/40 bg-muted/10 px-5 text-3xl font-bold text-center text-foreground focus:outline-none focus:ring-4 focus:ring-sky-200 focus:border-sky-400"
            placeholder={param.valore}
            value={valore}
            onChange={(e) => setValore(e.target.value)}
            data-testid="input-valore"
          />

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 h-12 rounded-2xl text-base"
              onClick={onClose}
              data-testid="input-modal-annulla"
            >
              Annulla
            </Button>
            <Button
              className="flex-1 h-12 rounded-2xl text-base gap-2 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
              onClick={handleSave}
              disabled={!valore.trim()}
              data-testid="input-modal-salva"
            >
              <Check className="w-5 h-5" />Salva
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ══════ PAGINA PRINCIPALE ══════ */
export default function PatientVitalsMonitor() {
  const [editingParam, setEditingParam] = useState(null);

  const handleSave = (paramId, valore) => {
    toast.success('Misurazione salvata', { description: `${paramId}: ${valore} registrato correttamente.` });
  };

  return (
    <AppLayout role="paziente">
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="patient-vitals-monitor">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-rose-50 flex items-center justify-center">
            <Activity className="w-5 h-5 text-rose-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="page-title">La mia Salute</h1>
            <p className="text-sm text-muted-foreground">Parametri vitali e telemonitoraggio</p>
          </div>
        </div>

        {/* Banner Teleconsulto */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <button
            className="w-full flex items-center gap-4 p-5 rounded-3xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all active:scale-[0.99]"
            onClick={() => toast.success('Stanza teleconsulto aperta', { description: 'Connessione in corso...' })}
            data-testid="btn-teleconsulto-stanza"
          >
            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm flex-shrink-0">
              <Video className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-lg font-bold">Entra in Stanza Teleconsulto</p>
              <p className="text-sm text-white/70">Videochiamata con il tuo medico — Prossimo: Ven 21/02 ore 14:30</p>
            </div>
            <TrendingUp className="w-6 h-6 text-white/40 flex-shrink-0" />
          </button>
        </motion.div>

        {/* Griglia Parametri */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="vitals-grid">
          {parametriVitali.map((p, i) => (
            <VitalCard key={p.id} param={p} index={i} onAdd={(param) => setEditingParam(param)} />
          ))}
        </div>
      </div>

      {/* Modale Inserimento */}
      <AnimatePresence>
        {editingParam && (
          <InputModal
            param={editingParam}
            onClose={() => setEditingParam(null)}
            onSave={handleSave}
          />
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
